import { useState, useRef, useEffect, type JSX } from "react";
import { useAuth } from "../../context/authCore";
import UserMenu from "./UserMenu";
import ProfileModal from "./ProfileModal";

type User = {
  displayName: string;
  photoURL: string;
};

type UserProfileProps = {
  compact?: boolean;
};

const UserProfile = ({
  compact = false,
}: UserProfileProps): JSX.Element | null => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  const usr = user as unknown as User;

  return (
    <div className="position-relative" ref={profileRef}>
      <div
        className="d-flex align-items-center gap-2"
        style={{ cursor: "pointer" }}
        onClick={() => setMenuOpen((prev) => !prev)}
      >
        {compact ? (
          <img
            src={usr.photoURL}
            alt="User"
            className="rounded-circle"
            style={{
              width: "35px",
              height: "35px",
              objectFit: "cover",
              border: "2px solid #00c6ff",
            }}
          />
        ) : (
          <>
            <span
              className="text-white"
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: "1rem",
              }}
            >
              Hello, {usr.displayName.split(" ")[0]}
            </span>
            <div
              style={{ width: "40px", height: "40px", position: "relative" }}
            >
              {!isLoaded && (
                <div
                  className="skeleton-loader"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(90deg, #444 25%, #666 50%, #444 75%)",
                    backgroundSize: "200% 100%",
                    animation: "skeletonPulse 1.2s ease-in-out infinite",
                  }}
                />
              )}
              <img
                src={usr.photoURL}
                alt="User"
                onLoad={() => setIsLoaded(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                  border: "2px solid #00c6ff",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  visibility: isLoaded ? "visible" : "hidden",
                }}
              />
            </div>
          </>
        )}
      </div>

      {menuOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            background: "#000",
            border: "1px solid #333",
            borderRadius: "0.5rem",
            padding: "0.5rem",
            zIndex: 9999,
          }}
        >
          <UserMenu
            onClose={() => setMenuOpen(false)}
            onOpenProfile={() => setShowProfileModal(true)}
          />
        </div>
      )}

      {showProfileModal && (
        <ProfileModal onClose={() => setShowProfileModal(false)} />
      )}

      <style>
        {`@keyframes skeletonPulse { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}
      </style>
    </div>
  );
};

export default UserProfile;
